import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type ActionStatus = 'pending' | 'running' | 'complete' | 'error';

export interface VoiceAction {
  actionId: string;
  tool: string;
  description: string;
  status: ActionStatus;
  result?: string;
  timestamp: string;
}

@customElement('cp-action-log')
export class CpActionLog extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) actions: VoiceAction[] = [];

  updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('actions')) {
      this._scrollToBottom();
    }
  }

  private _scrollToBottom(): void {
    requestAnimationFrame(() => {
      const container = this.querySelector('.cp-action-log__list');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  private _renderStatusIcon(status: ActionStatus) {
    switch (status) {
      case 'pending':
        return html`<span class="cp-action-log__icon cp-action-log__icon--pending" title="Ausstehend">&#9711;</span>`;
      case 'running':
        return html`<span class="cp-action-log__icon cp-action-log__icon--running" title="Laeuft">&#9881;</span>`;
      case 'complete':
        return html`<span class="cp-action-log__icon cp-action-log__icon--complete" title="Fertig">&#10003;</span>`;
      case 'error':
        return html`<span class="cp-action-log__icon cp-action-log__icon--error" title="Fehler">&#10007;</span>`;
    }
  }

  private _formatTime(timestamp: string): string {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '';
    }
  }

  render() {
    if (this.actions.length === 0) {
      return nothing;
    }

    return html`
      <div class="cp-action-log">
        <h3 class="cp-action-log__title">Agent-Aktionen</h3>
        <div class="cp-action-log__list">
          ${this.actions.map(action => html`
            <div class="cp-action-log__entry cp-action-log__entry--${action.status}">
              ${this._renderStatusIcon(action.status)}
              <div class="cp-action-log__content">
                <span class="cp-action-log__tool">${action.tool}</span>
                <span class="cp-action-log__desc">${action.description}</span>
                ${action.result ? html`<span class="cp-action-log__result">${action.result}</span>` : nothing}
              </div>
              <span class="cp-action-log__time">${this._formatTime(action.timestamp)}</span>
            </div>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-action-log': CpActionLog;
  }
}
